import {
  CreateInterventionReportRequest,
  GetUsersResponse,
  CreateReportResponse,
  PlayerProfile,
  ReportSignature,
  UserType,
  InterventionReport,
  UpdateInterventionReportResponse,
} from '@inplay/models';
import FullscreenModal from '../fullscreenModal/fullscreenModal';
import Input from '../Input/input';
import styles from './interventionReportForm.module.css';
import clock from '../../assets/clock.png';
import money from '../../assets/money.png';
import face from '../../assets/face.png';
import dice from '../../assets/dice.png';
import Button from '../button/button';
import { useOrganizationContext } from '../../service/context/organizationContext';
import { combineDateAndTime, getFormattedTimeValues } from '../../utils/helper';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import DatePickerV2 from '../datePickerV2/datePicker';
import { Mode } from '../datePickerV2/datePicker.type';
import TimePickerV3 from '../timePickerV3/timePicker';
import { Trans, useTranslation } from 'react-i18next';
import {
  genderOptions,
  ageOptions,
  ethinicityOptions,
  lengthOfPlayOptions,
  moneyOptions,
  behaviourOptions,
  otherOptions,
  actionTakenOptions,
  followUpOptions,
  Ethnicity,
  ethnicityTooltipContents,
} from '../reportDetails/constants';
import { useUsersSummary } from '../../hooks/useUserData';
import { FormState, Props } from './interventionReportForm.types';
import { Option } from '../dropDownInput/dropDownInput.type';
import { DateRange } from 'react-day-picker';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import moment from 'moment';
import SignatureModal from '../signatureModal/signatureModal';
import { ReportService } from '../../service/reportService';
import LinkHarmReportsModal from '../linkHarmReportsModal/linkHarmReportsModal';
import { UserService } from '../../service/userService';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import TooltipV2 from '../tooltipV2/tooltipV2';
import MultiSelectDropdown from '../multiSelectDropdown/multiSelectDropdown';
import RadioGroupV2 from '../radioGroup';
import { toastV2 } from '../toastV2/toastV2';
import { timeStringToMinutes } from '../harmReportForm/harmReportFormHelper';
import dayjs, { Dayjs } from 'dayjs';

const isValid = (formState: FormState): Boolean => {
  const hasCreatedBySignature = !!formState.createdBySignature;
  const hasReviewedBySignature =
    formState.userType === UserType.VENUE_STAFF
      ? true
      : !!formState.reviewedBySignature;

  return (
    (isCompletedByValid(formState) && hasCreatedBySignature) ||
    isReviewedByValid(formState) ||
    hasReviewedBySignature
  );
};

const isCompletedByValid = (formState: FormState): Boolean => {
  return (
    formState.interventionConductedBy !== '' &&
    formState.interventionDate !== undefined &&
    formState.interventionTime !== '' &&
    formState.description.trim() !== '' &&
    (formState.lengthOfPlay.length > 0 ||
      formState.money.length > 0 ||
      formState.behaviour.length > 0 ||
      formState.other.length > 0) &&
    formState.actionTaken.length > 0 &&
    formState.notesObservation.trim() !== ''
  );
};

const isReviewedByValid = (formState: FormState): Boolean => {
  return (
    formState?.userType === UserType.VENUE_STAFF ||
    (formState.staffCompliance !== '' &&
      formState.followUpAction.length > 0 &&
      formState.createdBySignature !== null)
  );
};

const isSameDayAsToday = (d: Dayjs) => d.isSame(dayjs(), 'day');

const InterventionReportForm = (props: Props) => {
  const { t } = useTranslation();
  const { selectedVenueBranch } = useOrganizationContext();
  const [showCompletedByAddSignature, setShowCompletedByAddSignature] =
    useState(false);
  const [showReviewedByAddSignature, setShowReviewedByAddSignature] =
    useState(false);
  const { usersSummary } = useUsersSummary(true);
  const { userDetail } = useUserDetailContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkedIds, setLinkedIds] = useState<string[]>(
    props.linkedHarmReportIds ?? [],
  );
  const [linkedRefNos, setLinkedRefNos] = useState<string[]>(
    props.linkedHarmRefNos ?? [],
  );
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
  const labelTooltipRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs(new Date()));
  const [time, setTime] = useState<Dayjs | null>(dayjs(new Date()));

  // Derive the report author: prefer verified user over logged-in user
  const reportAuthor: GetUsersResponse | undefined = (() => {
    if (!props.verifiedUser) return userDetail;
    if (props.verifiedUser.userId === userDetail?.id) return userDetail;
    const found = usersSummary?.data?.find(
      (u: GetUsersResponse) => u.id === props.verifiedUser?.userId,
    );
    if (found) return found;
    return {
      id: props.verifiedUser.userId,
      firstName: props.verifiedUser.firstName,
      lastName: props.verifiedUser.lastName,
      type: props.verifiedUser.type,
      email: props.verifiedUser.email,
    } as GetUsersResponse;
  })();

  // Fetch signature for verified user if different from logged-in user
  const [verifiedUserSignature, setVerifiedUserSignature] =
    useState<string>('');
  useEffect(() => {
    if (
      props.verifiedUser &&
      props.verifiedUser.userId !== userDetail?.id &&
      !reportAuthor?.signature
    ) {
      new UserService()
        .getUserSignature(props.verifiedUser.userId)
        .then((sig) => setVerifiedUserSignature(sig));
    }
  }, [props.verifiedUser, userDetail?.id]);

  // Merge fetched signature into reportAuthor
  const reportAuthorWithSignature: GetUsersResponse | undefined = reportAuthor
    ? {
        ...reportAuthor,
        signature: reportAuthor.signature || verifiedUserSignature,
      }
    : undefined;

  useEffect(() => {
    if (reportAuthor) {
      setFormState((prev: FormState) => {
        return {
          ...prev,
          interventionConductedBy: reportAuthor.id ?? '',
        };
      });
    }
  }, [reportAuthor]);

  const formattedTimeValues = getFormattedTimeValues(
    {
      uninterruptedTime: props.data
        ? props.data.playerDetails.gambling.uninterruptedTime
        : Number(props.player?.currentUninterruptedTime),
      totalTimeInGameArea: props.data
        ? props.data.playerDetails.gambling.totalTimeInGameArea
        : Number(props.player?.minutesInGamingRoom),
      totalTimeOutsideGameArea: props.data
        ? props.data.playerDetails.gambling.totalTimeOutsideGameArea
        : Number(props.player?.minutesOutsideGamingRoom),
    },
    t,
  );

  const initialFormState: FormState = props.data
    ? {
        userType: reportAuthor?.type,
        venueName: selectedVenueBranch?.name ?? '',
        interventionConductedBy: reportAuthor?.id ?? '',
        interventionDate: props.data.conductedAt,
        interventionTime: dayjs(props.data.conductedAt).format('HH:mm'),
        nickname: props.data.playerDetails.name,
        description: props.data.playerDetails.description,
        gender: props.data.playerDetails.gender,
        age: props.data.playerDetails.age,
        ethnicity: props.data.playerDetails.ethnicity,
        currentUninterruptedTime: formattedTimeValues.currentUninterruptedTime,
        gamblingAtmUse:
          props.data.playerDetails.gambling.gamblingATMUse.toString(),
        currentConsecutiveSweeps: props.consecutiveSweeps ?? '0',
        totalInterventionsOffered:
          props.data.playerDetails.gambling.interventionsOffered,
        totalInterventionsRequested:
          props.data.playerDetails.gambling.interventionsRequested,
        totalTimeInGambling: formattedTimeValues.totalTimeInGambling,
        totalTimeOutside: formattedTimeValues.totalTimeOutside,
        lengthOfPlay: props.data.signsOfGamblingHarm?.lengthOfPlay,
        money: props.data.signsOfGamblingHarm?.money,
        behaviour: props.data.signsOfGamblingHarm?.behaviour,
        other: props.data.signsOfGamblingHarm?.other,
        actionTaken: props.data.interventionAction ?? [],
        actionTakenDetails: '', // TODO: Add during create
        notesObservation: props.data.createdBy?.notes ?? '',
        staffCompliance: props.data.staffComplied ? 'yes' : 'no',
        followUpAction: props.data.followUpAction ?? [],
        followUpActionDetails: '', // TODO: Add during create
        createdBySignature: props.data.createdBy ?? null,
        comments: props.data.reviewedBy?.notes ?? '',
        reviewedBySignature: props.data.reviewedBy ?? null,
      }
    : {
        // Player info only available
        userType: reportAuthor?.type,
        venueName: selectedVenueBranch?.name ?? '',
        interventionConductedBy: reportAuthor?.id ?? '',
        interventionDate: new Date(),
        interventionTime: dayjs().format('HH:mm'),
        nickname: '',
        description: props.player?.description ?? '',
        gender: '',
        age: '',
        ethnicity: undefined,
        currentUninterruptedTime: formattedTimeValues.currentUninterruptedTime,
        gamblingAtmUse: props.player?.totalWithdrawals.toString() ?? '00  ',
        currentConsecutiveSweeps: props.consecutiveSweeps ?? '0',
        totalInterventionsOffered: props.interventionOffered
          ? props.interventionOffered.toString().padStart(2, '0')
          : '00',
        totalInterventionsRequested: props.interventionRequested
          ? props.interventionRequested.toString().padStart(2, '0')
          : '00',
        totalTimeInGambling: formattedTimeValues.totalTimeInGambling,
        totalTimeOutside: formattedTimeValues.totalTimeOutside,
        lengthOfPlay: [],
        money: [],
        behaviour: [],
        other: [],
        actionTaken: [],
        actionTakenDetails: '',
        notesObservation: '',
        staffCompliance: '',
        followUpAction: [],
        followUpActionDetails: '',
        createdBySignature: null,
        comments: '',
        reviewedBySignature: null,
      };

  const [formState, setFormState] = useState<FormState>(initialFormState);

  const playerFaceImage: string | undefined = props.data
    ? props.data.playerProfile.faceImage
    : props.player?.faceImage;
  const playerBodyImage: string | undefined = props.data
    ? props.data.playerProfile.bodyImage
    : props.player?.fullImage;

  // Handlers
  const handleInputChange = (name: string, value: string) => {
    setFormState((prev: FormState) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (
    field: keyof FormState,
    value: string | string[],
  ) => {
    setFormState((prev: FormState) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked, id } = e.target;
      const current = Array.isArray(formState[field])
        ? (formState[field] as string[])
        : [];

      const nextFlags = checked
        ? [...current, id]
        : current.filter((v: string) => v !== id);

      handleSelectChange(field, nextFlags);
    };

  const handleDateChange = (date?: Date) => {
    setFormState((prev: FormState) => ({
      ...prev,
      interventionDate: date,
    }));
  };

  const handleTimeChange = (time: string) => {
    setFormState((prev: FormState) => ({
      ...prev,
      interventionTime: time,
    }));
  };

  const handleRadioChange = (value: string) => {
    setFormState((prev: FormState) => ({
      ...prev,
      staffCompliance: value,
    }));
  };

  const handleSubmit = async () => {
    if (!isValid(formState)) return;
    try {
      setIsLoading(true);

      if (!reportAuthor || !formState.createdBySignature) {
        return;
      }

      let playerProfile: PlayerProfile = {
        id:
          (props.data ? props.data.playerProfile.id : props.player?.playerId) ??
          '',
      };

      if (props.data) {
        playerProfile.faceImageId = props.data.playerProfile.faceImageId;
        playerProfile.bodyImageId = props.data.playerProfile.bodyImageId;
      } else if (props.player) {
        playerProfile.faceImageId = `players/face_${props.player.playerId}_${props.player.firstEventId}.jpeg`;
        playerProfile.bodyImageId = `players/full_${props.player.playerId}_${props.player.firstEventId}.jpeg`;
      }

      const createdBySignature = {
        ...formState.createdBySignature,
        notes: formState.notesObservation,
      };

      const reviewedBySignature = formState.reviewedBySignature
        ? {
            ...formState.reviewedBySignature,
            notes: formState.comments,
          }
        : undefined;

      const managerSectionDetails =
        reportAuthor.type == UserType.VENUE_STAFF
          ? {}
          : {
              staffComplied: formState.staffCompliance === 'yes',
              followUpAction: formState.followUpAction,
            };

      const reportService = new ReportService();

      if (props.data) {
        const interventionReportPayload: InterventionReport = {
          ...props.data,
          conductedAt: new Date(
            combineDateAndTime(
              selectedDate.toDate(),
              time ? time.format('HH:mm') : dayjs(new Date()).format('HH:mm'),
            ),
          ),
          playerProfile: {
            ...props.data.playerProfile,
            faceImage: undefined,
            bodyImage: undefined,
          },
          playerDetails: {
            name: formState.nickname,
            description: formState.description,
            gender:
              (formState.gender as 'Male' | 'Female' | 'Other' | undefined) ??
              props.data.playerDetails.gender,
            age:
              (formState.age as
                | '20 - 30'
                | '30 - 40'
                | '40 - 50'
                | '50 - 60'
                | '60+'
                | undefined) ?? props.data.playerDetails.age,
            ethnicity: formState.ethnicity,
            gambling: props.data.playerDetails.gambling,
          },
          signsOfGamblingHarm: {
            lengthOfPlay: formState.lengthOfPlay,
            money: formState.money,
            behaviour: formState.behaviour,
            other: formState.other,
          },
          interventionAction: formState.actionTaken,
          createdBy: createdBySignature,
          staffComplied: formState.staffCompliance === 'yes',
          followUpAction: formState.followUpAction,
          reviewedBy: reviewedBySignature,
        };
        console.log(interventionReportPayload);

        const response: UpdateInterventionReportResponse =
          await reportService.editInterventionReport({
            originalInterventionReportId: props.data.originalReportId ?? '',
            interventionReport: interventionReportPayload,
          });
        console.log(response);

        if (props.isReviewAndEditMode) {
          toastV2.success('Review completed successfully');
        } else {
          toastV2.success('Intervention Report was updated successfully');
        }
        props.onSuccess?.();
      } else {
        const apiRequestPayload: CreateInterventionReportRequest = {
          venueBranchId: selectedVenueBranch?._id || '',
          conductedBy: formState.interventionConductedBy,
          conductedAt: combineDateAndTime(
            selectedDate.toDate(),
            time ? time.format('HH:mm') : dayjs(new Date()).format('HH:mm'),
          ),
          playerProfile,
          playerDetails: {
            name: formState.nickname,
            description: formState.description,
            gender: formState.gender as 'Male' | 'Female' | 'Other',
            age: formState.age as
              | '20 - 30'
              | '30 - 40'
              | '40 - 50'
              | '50 - 60'
              | '60+',
            ethnicity: formState.ethnicity,
            gambling: {
              uninterruptedTime: timeStringToMinutes(
                formState.currentUninterruptedTime,
              ),
              gamblingATMUse: Number(formState.gamblingAtmUse),
              consecutiveSweepsCount: Number(props.consecutiveSweeps),
              interventionsOffered: 0, // TODO: Integrate API from Harm Report
              interventionsRequested: 0,
              totalTimeInGameArea: timeStringToMinutes(
                formState.totalTimeInGambling,
              ),
              totalTimeOutsideGameArea: timeStringToMinutes(
                formState.totalTimeOutside,
              ),
            },
          },
          signsOfGamblingHarm: {
            lengthOfPlay: formState.lengthOfPlay,
            money: formState.money,
            behaviour: formState.behaviour,
            other: formState.other,
          },
          interventionAction: formState.actionTaken,
          createdBy: createdBySignature,
          createdAt: formState.createdBySignature.signedDate.toISOString(),
          reviewedBy: reviewedBySignature,
          ...(linkedIds.length && { harmReportIds: linkedIds }),
          ...managerSectionDetails,
        };
        const response: CreateReportResponse =
          await reportService.createInterventionReport(apiRequestPayload);

        console.log(response);
        toastV2.success('Intervention Report created successfully');
        props.onSuccess?.();
      }
    } catch (error) {
      console.error(error);
      toastV2.success('Failed to create Intervention Report');
    } finally {
      setIsLoading(false);
      props.onBack();
    }
  };

  return (
    <FullscreenModal
      show={true}
      onBack={props.onBack}
      title={props.title}
      modalStyle={styles.modal}
      headerRight={
        <div className={styles.headerRight}>
          <Button className={styles.outlinedButton} onClick={props.onBack}>
            <Trans i18nKey={'createInterventionReport.cancel'} />
          </Button>
          <Button
            className={styles.button}
            onClick={handleSubmit}
            disabled={!isValid(formState)}
          >
            {isLoading ? (
              <ButtonSpinner />
            ) : (
              <Trans i18nKey={'createInterventionReport.submit'} />
            )}
          </Button>
        </div>
      }
    >
      <SignatureModal
        isOpen={showCompletedByAddSignature}
        handleAddSignature={(data: ReportSignature) => {
          setFormState((prev: FormState) => ({
            ...prev,
            createdBySignature: data,
          }));
          setShowCompletedByAddSignature(false);
        }}
        loading={false}
        editingSignature={formState.createdBySignature}
        onClose={() => setShowCompletedByAddSignature(false)}
        userDetail={reportAuthorWithSignature}
        isReviewing={false}
      />
      <SignatureModal
        isOpen={showReviewedByAddSignature}
        handleAddSignature={(data: ReportSignature) => {
          setFormState((prev: FormState) => ({
            ...prev,
            reviewedBySignature: data,
          }));
          setShowReviewedByAddSignature(false);
        }}
        loading={false}
        editingSignature={formState.reviewedBySignature}
        onClose={() => setShowReviewedByAddSignature(false)}
        userDetail={reportAuthorWithSignature}
        isReviewing={true}
      />
      {showLinkModal && props.player && (
        <LinkHarmReportsModal
          playerId={props.player.playerId}
          selectedIds={linkedIds}
          onClose={() => setShowLinkModal(false)}
          onSkip={() => setShowLinkModal(false)}
          onLink={(ids, refNos) => {
            setLinkedIds(ids);
            setLinkedRefNos(refNos);
            setShowLinkModal(false);
          }}
        />
      )}
      <div className={styles.content}>
        {/* General Info */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.generalInfo'} /> *
          </div>
          <div className={styles.inputs}>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.venue'} />
              </label>
              <Input
                value={formState.venueName}
                disabled
                placeholder="Venue Name"
                type="text"
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans
                  i18nKey={'createInterventionReport.interventionConductedBy'}
                />
              </label>
              <MultiSelectDropdown
                options={
                  usersSummary
                    ? [
                        {
                          label: `${reportAuthor?.firstName} ${reportAuthor?.lastName}`,
                          value: reportAuthor?.id ?? '',
                        },
                        ...usersSummary.data.map((user: GetUsersResponse) => ({
                          label: `${user.firstName} ${user.lastName}`,
                          value: user.id ?? '',
                        })),
                      ].filter((option: Option) => option.value)
                    : []
                }
                search
                selected={formState.interventionConductedBy}
                onSelect={(val: string | string[]) =>
                  handleSelectChange('interventionConductedBy', val as string)
                }
                placeholder="Firstname Lastname"
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans
                  i18nKey={'createInterventionReport.interventionConductedOn'}
                />
              </label>
              <div className={styles.inputGroup}>
                <DatePickerV2
                  finalRange={{
                    from: formState.interventionDate,
                    to: undefined,
                  }}
                  handleApply={(range: DateRange) => {
                    handleDateChange(range.from);
                    setSelectedDate(dayjs(range.from));
                  }}
                  mode={Mode.Single}
                />
                <TimePickerV3
                  date={selectedDate}
                  isDateToday={isSameDayAsToday(selectedDate)}
                  value={time}
                  onChange={(time: Dayjs | null) => {
                    if (time) handleTimeChange(time.format('HH:mm'));
                    setTime(time);
                  }}
                  disabled={false}
                  ampm={true}
                />
              </div>
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.harmInstances'} />
              </label>
              <Input
                value={props.harmCount?.toString() ?? '0'}
                disabled
                type="text"
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.linkedHarmReports'} />
              </label>
              <div
                onClick={() => setShowLinkModal(true)}
                className="w-full rounded-full px-4 py-2 text-sm cursor-pointer bg-[#FAFAFA] border border-transparent hover:border-[#561BDC] flex items-center justify-between"
              >
                <span
                  className={
                    linkedRefNos.length ? 'text-[#151740]' : 'text-gray-400'
                  }
                >
                  {linkedRefNos.length ? linkedRefNos.join(', ') : 'Select'}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7C7AA8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Player Photos */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.playersPhoto'} />
          </div>
          <div className={styles.portrait}>
            <label className={styles.label}>
              <Trans i18nKey={'createInterventionReport.portrait'} />
            </label>
            {playerFaceImage && <img src={playerFaceImage} alt="Face" />}
          </div>

          <div className={styles.fullBody}>
            <label className={styles.label}>
              <Trans i18nKey={'createInterventionReport.fullBody'} />
            </label>
            {playerBodyImage && <img src={playerBodyImage} alt="Full" />}
          </div>
        </div>

        {/* Player Details */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.playersDetails'} />
          </div>
          <div className={styles.inputs}>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.nickName'} />
              </label>
              <Input
                value={formState.nickname}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('nickname', event.target.value)
                }
                placeholder="Enter Name"
                type="text"
              />
            </div>
            <div className={styles.input} style={{ gridColumn: 'span 2' }}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.description'} /> *
              </label>
              <Input
                value={formState.description}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('description', event.target.value)
                }
                placeholder="Enter Description"
                type="text"
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.gender'} />
              </label>
              <MultiSelectDropdown
                options={genderOptions}
                selected={formState.gender}
                onSelect={(val: string | string[]) =>
                  handleSelectChange('gender', val as string)
                }
                placeholder="Select"
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.age'} />
              </label>
              <MultiSelectDropdown
                options={ageOptions}
                selected={formState.age}
                onSelect={(val: string | string[]) =>
                  handleSelectChange('age', val as string)
                }
                placeholder="Select"
              />
            </div>
            <div className={styles.input}>
              <label
                className={styles.label}
                ref={labelTooltipRef}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Trans i18nKey={'createInterventionReport.ethnicity'} />{' '}
                <TooltipV2
                  relativeContainerRef={labelTooltipRef}
                  child={<div className={styles.tooltipButton}>i</div>}
                  tooltipContent={
                    <div className={styles.tooltipContent}>
                      {ethnicityTooltipContents.map((tooltipContent, index) => (
                        <div key={index} className={styles.tooltipSection}>
                          <div className={styles.tooltipTitle}>
                            {tooltipContent.title}
                          </div>
                          <ul
                            className={styles.tooltipList}
                            style={{
                              marginBottom:
                                index === ethnicityTooltipContents.length - 1
                                  ? '0px'
                                  : '10px',
                            }}
                          >
                            {tooltipContent.points.map((point, idx) => (
                              <li key={idx} className={styles.tooltipPoint}>
                                <Trans i18nKey={point} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  }
                />
              </label>
              <MultiSelectDropdown
                options={ethinicityOptions}
                selected={formState.ethnicity}
                onSelect={(val: string | string[]) =>
                  handleSelectChange('ethnicity', val as Ethnicity)
                }
                placeholder="Select"
              />
            </div>

            {/* Disabled fields */}
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans
                  i18nKey={'createInterventionReport.currentUninterrupted'}
                />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.currentUninterruptedTime}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.gamblingAtmUse'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.gamblingAtmUse}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans
                  i18nKey={'createInterventionReport.currentConsecutive'}
                />
              </label>
              <Input
                placeholder=""
                type="text"
                value={String(formState.currentConsecutiveSweeps)}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.totalIntervention'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={
                  '00'.toString().padStart(2, '0') +
                  '/' +
                  '00'.toString().padStart(2, '0')
                } // TODO: Add state variable for Intervention details
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans
                  i18nKey={'createInterventionReport.totalTimeInGambling'}
                />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.totalTimeInGambling}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createInterventionReport.totalTimeOutside'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.totalTimeOutside}
                disabled
              />
            </div>
          </div>
        </div>

        {/* Signs of Gambling Harm */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.signsOfGamblingHarm'} /> *
          </div>
          <div className={styles.inputs}>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={clock} alt="" />{' '}
                <Trans i18nKey={'createInterventionReport.lengthOfPlay'} />
              </label>
              <MultiSelectDropdown
                options={lengthOfPlayOptions}
                selected={formState.lengthOfPlay}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('lengthOfPlay', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
              />
            </div>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={money} alt="" />{' '}
                <Trans i18nKey={'createInterventionReport.money'} />
              </label>
              <MultiSelectDropdown
                options={moneyOptions}
                selected={formState.money}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('money', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
              />
            </div>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={face} alt="" />{' '}
                <Trans i18nKey={'createInterventionReport.behaviour'} />
              </label>
              <MultiSelectDropdown
                options={behaviourOptions}
                selected={formState.behaviour}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('behaviour', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
              />
            </div>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={dice} alt="" />
                <Trans i18nKey={'createInterventionReport.other'} />
              </label>
              <MultiSelectDropdown
                options={otherOptions}
                selected={formState.other}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('other', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
              />
            </div>
          </div>
        </div>

        {/* Action Taken */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.actionTaken'} /> *
          </div>
          <div className={styles.inputs}>
            <div className={styles.input} style={{ gridColumn: 'span 3' }}>
              {actionTakenOptions.map((action) => {
                const checked = formState.actionTaken.includes(action.value);

                return (
                  <div className={styles.checkInput} key={action.value}>
                    <div className={styles.checkboxWrapper}>
                      <input
                        className={styles.customCheckbox}
                        id={action.value}
                        type="checkbox"
                        checked={checked}
                        onChange={handleCheckboxChange('actionTaken')}
                      />
                      <svg
                        className={styles.checkmark}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <label htmlFor={action.value} className={styles.label}>
                      <Trans i18nKey={action.label} />
                    </label>
                  </div>
                );
              })}
            </div>

            <div className={styles.input} style={{ gridColumn: 'span 3' }}>
              <Input
                placeholder="Enter details"
                value={formState.actionTakenDetails}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('actionTakenDetails', event.target.value)
                }
                type="text"
                disabled={
                  !formState.actionTaken.includes(
                    'createInterventionReport.actionTakenOptions.other',
                  )
                }
              />
            </div>
          </div>
        </div>

        {/* Notes & Observations */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.notes&Observation'} /> *
          </div>
          <div className={styles.notesSection}>
            <label className={styles.label}>
              <Trans
                i18nKey={
                  'createInterventionReport.notes&ObservationOptions.whatWeSee'
                }
              />
            </label>
            <textarea
              name="notesObservation"
              placeholder="Enter details"
              className={styles.details}
              value={formState.notesObservation}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                handleInputChange('notesObservation', event.target.value)
              }
            />
            <ul>
              <li className={styles.bulletPoint}>
                •{' '}
                <Trans
                  i18nKey={
                    'createInterventionReport.notes&ObservationOptions.beSpecific'
                  }
                />
              </li>
              <li className={styles.bulletPoint}>
                •{' '}
                <Trans
                  i18nKey={
                    'createInterventionReport.notes&ObservationOptions.playerDescriptions'
                  }
                />
              </li>
              <li className={styles.bulletPoint}>
                •{' '}
                <Trans
                  i18nKey={
                    'createInterventionReport.notes&ObservationOptions.describeHow'
                  }
                />
              </li>
            </ul>
          </div>
        </div>

        {/* Completed By */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createInterventionReport.completedBy'} /> *
          </div>
          <div className={styles.inputs}>
            {formState.createdBySignature ? (
              <div className={styles.signaturePreview}>
                <div className={styles.details}>
                  <img
                    src={formState.createdBySignature.signature}
                    alt="signature"
                    width={150}
                  />
                  <div className={styles.userDetail}>
                    {formState.createdBySignature.firstName}{' '}
                    {formState.createdBySignature.lastName}
                  </div>
                  <div className={styles.userDetail}>
                    {moment(formState.createdBySignature.signedDate).format(
                      'DD/MM/YYYY, hh:mm A',
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Button
                className={styles.button}
                onClick={() => setShowCompletedByAddSignature(true)}
                disabled={!isCompletedByValid(formState)}
              >
                <Trans i18nKey={'createInterventionReport.addSignature'} />
              </Button>
            )}
          </div>
        </div>

        {reportAuthor?.type !== UserType.VENUE_STAFF && (
          <>
            {/* Manager Review */}
            <div className={`${styles.cell} ${styles.managerCell}`}>
              <div className={styles.Heading}>
                <Trans i18nKey={'createInterventionReport.managerReview'} />
              </div>
            </div>

            {/* Staff Compliance */}
            <div className={`${styles.cell} ${styles.managerCell}`}>
              <div className={styles.title}>
                <Trans i18nKey={'createInterventionReport.staffCompliance'} /> *
              </div>
              <div className={styles.radioSection}>
                <label className={styles.label}>
                  <Trans i18nKey={'createInterventionReport.hasStaffMember'} />
                </label>
                <RadioGroupV2
                  options={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                  name="compliance"
                  value={formState.staffCompliance}
                  onChange={handleRadioChange}
                  horizontal
                />
              </div>
            </div>

            {/* Follow Up Actions */}
            <div className={`${styles.cell} ${styles.managerCell}`}>
              <div className={styles.title}>
                <Trans i18nKey={'createInterventionReport.followUpActions'} /> *
              </div>
              <div className={styles.inputs}>
                <div className={styles.input} style={{ gridColumn: 'span 3' }}>
                  {followUpOptions.map((action) => {
                    const checked = formState.followUpAction.includes(
                      action.value,
                    );

                    return (
                      <div className={styles.checkInput} key={action.value}>
                        <div className={styles.checkboxWrapper}>
                          <input
                            id={action.value}
                            type="checkbox"
                            checked={checked}
                            onChange={handleCheckboxChange('followUpAction')}
                            className={styles.customCheckbox}
                          />
                          <svg
                            className={styles.checkmark}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>

                        <label htmlFor={action.value} className={styles.label}>
                          <Trans i18nKey={action.label} />
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.input} style={{ gridColumn: 'span 3' }}>
                  <Input
                    placeholder="Enter details"
                    type="text"
                    value={formState.followUpActionDetails}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('followUpDetails', event.target.value)
                    }
                    disabled={
                      !formState.followUpAction.includes(
                        'createInterventionReport.followUpOptions.other',
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Comments */}
            <div className={`${styles.cell} ${styles.managerCell}`}>
              <div className={styles.title}>
                <Trans i18nKey={'createInterventionReport.comments'} />
              </div>
              <div className={styles.notesSection}>
                <textarea
                  name="comments"
                  placeholder="Enter details"
                  className={styles.details}
                  value={formState.comments}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('comments', event.target.value)
                  }
                />
              </div>
            </div>

            {/* Reviewed By */}
            <div className={`${styles.cell} ${styles.managerCell}`}>
              <div className={styles.title}>
                <Trans i18nKey={'createInterventionReport.reviewedBy'} /> *
              </div>
              <div className={styles.inputs}>
                {formState.reviewedBySignature ? (
                  <div className={styles.signaturePreview}>
                    <div className={styles.details}>
                      <img
                        src={formState.reviewedBySignature.signature}
                        alt="signature"
                        width={150}
                      />
                      <div className={styles.userDetail}>
                        {formState.reviewedBySignature.firstName}{' '}
                        {formState.reviewedBySignature.lastName}
                      </div>
                      <div className={styles.userDetail}>
                        {moment(
                          formState.reviewedBySignature.signedDate,
                        ).format('DD/MM/YYYY, hh:mm A')}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button
                    className={styles.button}
                    onClick={() => setShowReviewedByAddSignature(true)}
                    disabled={!isReviewedByValid(formState)}
                  >
                    <Trans i18nKey={'createInterventionReport.addSignature'} />
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </FullscreenModal>
  );
};

export default InterventionReportForm;
